/**
 * Konwersja PUWG 2000 (Państwowy Układ Współrzędnych Geodezyjnych 2000) ↔ WGS84 (lat/lng).
 *
 * PUWG 2000 to projekcja kartograficzna używana w Polsce dla map szczegółowych
 * (1:5 000, 1:10 000, EGiB, BDOT10k). Cztery strefy 3-stopniowe:
 *   strefa 5 → centralny południk 15°E → EPSG:2176
 *   strefa 6 → centralny południk 18°E → EPSG:2177
 *   strefa 7 → centralny południk 21°E → EPSG:2178
 *   strefa 8 → centralny południk 24°E → EPSG:2179
 *
 * X (northing) ≈ 5 500 000–6 200 000 (rośnie na północ)
 * Y (easting)  ma prefiks numeru strefy: 5 xxx xxx (s5), 6 xxx xxx (s6), 7 xxx xxx (s7), 8 xxx xxx (s8)
 *
 * Skala odwzorowania: 0.999923 (mała deformacja, idealna dla 3° pasów).
 */
import proj4 from 'proj4';

export type Puwg2000Zone = 5 | 6 | 7 | 8;

const PUWG_2000_DEFS: Record<Puwg2000Zone, string> = {
  5: '+proj=tmerc +lat_0=0 +lon_0=15 +k=0.999923 +x_0=5500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  6: '+proj=tmerc +lat_0=0 +lon_0=18 +k=0.999923 +x_0=6500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  7: '+proj=tmerc +lat_0=0 +lon_0=21 +k=0.999923 +x_0=7500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  8: '+proj=tmerc +lat_0=0 +lon_0=24 +k=0.999923 +x_0=8500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
};

const WGS84 = '+proj=longlat +datum=WGS84 +no_defs';

/**
 * Heurystyczne wykrycie strefy PUWG 2000 z wartości Y (easting):
 *   Y w [5 000 000, 5 999 999] → strefa 5
 *   Y w [6 000 000, 6 999 999] → strefa 6
 *   Y w [7 000 000, 7 999 999] → strefa 7
 *   Y w [8 000 000, 8 999 999] → strefa 8
 * Zwraca null gdy Y poza zakresem.
 */
export function detectZoneFromY(y: number): Puwg2000Zone | null {
  if (!Number.isFinite(y)) return null;
  const prefix = Math.floor(y / 1_000_000);
  if (prefix === 5 || prefix === 6 || prefix === 7 || prefix === 8) {
    return prefix as Puwg2000Zone;
  }
  return null;
}

/**
 * Konwertuje współrzędne PUWG 2000 → WGS84.
 * @param zone Numer strefy (5/6/7/8).
 * @param x Northing w metrach (zwykle 5 500 000+).
 * @param y Easting w metrach z prefiksem strefy.
 * @returns { lat, lng } w stopniach dziesiętnych (WGS84) albo null gdy konwersja zawiodła.
 */
export function puwg2000ToWgs84(zone: Puwg2000Zone, x: number, y: number): { lat: number; lng: number } | null {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  try {
    // proj4 oczekuje [easting, northing] dla projected i zwraca [lng, lat] dla longlat
    const [lng, lat] = proj4(PUWG_2000_DEFS[zone], WGS84, [y, x]) as [number, number];
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    // Sanity check: lat 49–55 i lng 14–24 dla terytorium Polski
    if (lat < 48 || lat > 56 || lng < 13 || lng > 25) return null;
    return { lat, lng };
  } catch {
    return null;
  }
}

/**
 * Odwrotna konwersja: WGS84 → PUWG 2000 (zwykle dla preview).
 * Wybór strefy: auto na podstawie długości geograficznej.
 */
export function wgs84ToPuwg2000(lat: number, lng: number): { zone: Puwg2000Zone; x: number; y: number } | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const zone: Puwg2000Zone =
    lng < 16.5 ? 5 :
    lng < 19.5 ? 6 :
    lng < 22.5 ? 7 :
    8;
  try {
    const [y, x] = proj4(WGS84, PUWG_2000_DEFS[zone], [lng, lat]) as [number, number];
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { zone, x, y };
  } catch {
    return null;
  }
}
